import { Navigate, type RouteObject } from 'react-router';

import { OutletContextTypeGuard } from '@/app/routers/guards';
import {
  accountPath,
  isActiveAccountOutletContext,
  isWorkspaceOutletContext,
  ROOT_PATH,
  useActiveAccountContext,
  workspacePath,
} from '@/true-fsd/data/advertiser-account';
import {
  useDefaultAccount,
  useDefaultWorkspace,
} from '@/true-fsd/data/advertiser-account/react-query';
import {
  CreateAccount,
  CreateAccountCompleteActive,
} from '@/true-fsd/features/CreateAccount';
import {
  AddSiteToAccount,
  CreateAccountWithSite,
} from '@/true-fsd/features/CreateSite';
import { WorkspaceTypeSelect } from '@/true-fsd/features/WorkspaceTypeSelect';
import { WorkspaceLayout } from '@/true-fsd/layout/Workspace';
import { ButtonLink } from '@/true-fsd/shared/ui/ButtonLink';
import { FigmaWhiteLayout } from '@/true-fsd/shared/ui/FigmaLayout';

import { HistoryTreeWrite } from '../history-store/HistoryTreeWrite';
import { channelRequest } from './channel-request';
import { analytics } from './children/children/analytics';
import { settings } from './children/children/settings';
import { AddChannelToAccountPage } from '@/true-fsd/layout/AddChannelToAccount';
import { CreateAccountWithChannelPage } from '@/true-fsd/layout/CreateAccountWithChannel';
import { ReferralPage } from '@/true-fsd/layout/Referral';
import { referralManyLoader } from '@/true-fsd/data/referral/router/loader';
import { queryClient } from '@/app/providers/queryClient';

// eslint-disable-next-line react-refresh/only-export-components
const ToDefaultAccount = () => {
  const defaultAccount = useDefaultAccount();

  return (
    <Navigate
      to={defaultAccount ? accountPath(defaultAccount) : ROOT_PATH.accountNew}
      replace
    />
  );
};

// eslint-disable-next-line react-refresh/only-export-components
const ToDefaultWorkspace = () => {
  const { activeAccount } = useActiveAccountContext();
  const workspace = useDefaultWorkspace(activeAccount);

  // console.log({ C: 'ToDefaultWorkspace', activeAccount, workspace })

  return (
    <Navigate
      to={
        workspace
          ? workspacePath(workspace)
          : accountPath(activeAccount, 'workspace/new')
      }
      replace
    />
  );
};

export const account = {
  path: 'account',
  children: [
    {
      path: 'new',
      children: [
        {
          index: true,
          element: (
            <CreateAccount
              RenderLayout={FigmaWhiteLayout}
              onNewAccountUpdated={(navigate) => navigate(`workspace`)}
              description={
                <>
                  Если у вашей организации уже есть аккаунт,
                  <br /> то попросите администратора прислать
                  <br /> вам пригласительную ссылку для авторизации
                </>
              }
            />
          ),
        },
        {
          path: 'workspace',
          children: [
            {
              index: true,
              element: (
                <WorkspaceTypeSelect
                  RenderLayout={FigmaWhiteLayout}
                  RenderForward={({ value, navigate: __, ...props }) => (
                    <ButtonLink {...props} to={value} />
                  )}
                  RenderBack={({ value: _, navigate: __, ...props }) => (
                    <ButtonLink {...props} to={'..'} />
                  )}
                />
              ),
            },
            {
              path: 'site',
              element: (
                <CreateAccountWithSite
                  RenderLayout={FigmaWhiteLayout}
                  onSuccess={(navigate, { createdAccount, invite }) =>
                    navigate(accountPath(createdAccount, 'complete'), {
                      state: invite,
                    })
                  }
                  RenderBack={({ navigate: __, ...props }) => (
                    //to WorkspaceTypeSelect
                    <ButtonLink {...props} to={'..'} />
                  )}
                />
              ),
            },
            {
              path: 'channel',
              element: <CreateAccountWithChannelPage backLink={'..'} />,
            },
            {
              path: '*',
              element: <Navigate to=".." />,
            },
          ],
        },
      ],
    },
    {
      path: ':advertiser_account_id',
      element: (
        <OutletContextTypeGuard typeGuard={isActiveAccountOutletContext} />
      ),
      children: [
        {
          path: 'workspace',
          children: [
            {
              path: 'new',
              children: [
                {
                  index: true,
                  element: (
                    <WorkspaceTypeSelect
                      RenderLayout={FigmaWhiteLayout}
                      RenderForward={({ value, navigate: __, ...props }) => (
                        <ButtonLink {...props} to={value} />
                      )}
                      RenderBack={({ value: _, navigate: __, ...props }) => (
                        //to account
                        <ButtonLink {...props} to="../.." />
                      )}
                    />
                  ),
                },
                {
                  path: 'site',
                  element: (
                    <AddSiteToAccount
                      RenderLayout={FigmaWhiteLayout}
                      onSuccess={(navigate, ws) => navigate(workspacePath(ws))}
                      RenderBack={({ navigate: __, ...props }) => (
                        //to WorkspaceTypeSelect
                        <ButtonLink {...props} to={'..'} />
                      )}
                    />
                  ),
                },
                {
                  path: 'channel',
                  element: <AddChannelToAccountPage backLink={'..'} />,
                },
                {
                  path: '*',
                  element: <Navigate to=".." />,
                },
              ],
            },
            {
              path: ':workspace_type/:workspace_id',
              element: (
                <OutletContextTypeGuard typeGuard={isWorkspaceOutletContext} />
              ),
              children: [
                {
                  Component: WorkspaceLayout,
                  children: [
                    analytics,
                    settings,
                    {
                      path: 'referral',
                      loader: referralManyLoader(queryClient),
                      element: (
                        <HistoryTreeWrite>
                          <ReferralPage />
                        </HistoryTreeWrite>
                      ),
                    },

                    {
                      path: 'new-workspace',
                      children: [
                        {
                          index: true,
                          element: (
                            <WorkspaceTypeSelect
                              RenderLayout={FigmaWhiteLayout}
                              RenderForward={({
                                value,
                                navigate: __,
                                ...props
                              }) => <ButtonLink {...props} to={value} />}
                              RenderBack={({
                                value: _,
                                navigate: __,
                                ...props
                              }) => (
                                //to workspace prefix

                                <ButtonLink {...props} to={'..'} />
                              )}
                            />
                          ),
                        },
                        {
                          path: 'site',
                          element: (
                            <AddSiteToAccount
                              RenderLayout={FigmaWhiteLayout}
                              onSuccess={(navigate, ws) =>
                                navigate(workspacePath(ws))
                              }
                              RenderBack={({ navigate: __, ...props }) => (
                                //to WorkspaceTypeSelect
                                <ButtonLink {...props} to={'..'} />
                              )}
                            />
                          ),
                        },
                        {
                          path: 'channel',
                          element: <AddChannelToAccountPage backLink={'..'} />,
                        },
                        {
                          path: '*',
                          element: <Navigate to=".." />,
                        },
                      ],
                    },

                    {
                      index: true,
                      element: <Navigate to="analytics" />,
                    },
                    {
                      path: '*',
                      element: <Navigate to=".." />,
                    },
                  ],
                },
              ],
            },

            {
              index: true,
              element: <Navigate to=".." />,
            },
            {
              path: '*',
              element: <Navigate to=".." />,
            },
          ],
        },
        channelRequest,
        {
          path: 'complete',
          element: (
            <CreateAccountCompleteActive RenderLayout={FigmaWhiteLayout} />
          ),
        },
        {
          index: true,
          Component: ToDefaultWorkspace,
        },
        {
          path: '*',
          Component: ToDefaultWorkspace,
        },
      ],
    },
    {
      index: true,
      Component: ToDefaultAccount,
    },
    {
      path: '*',
      Component: ToDefaultAccount,
    },
  ],
} satisfies RouteObject;
